import { Box, Container, Paper, Typography, Link, Button as _LoadingButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { object, string, TypeOf } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginFormInput } from '../share';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import useAuth from "../hooks/useAuth";
import {useTitle} from 'react-use';
import { Trans } from "@lingui/react/macro";
import { t, msg } from "@lingui/core/macro";

import { getGoogleUrl } from '../utils/getGoogleUrl';
import { getGitHubUrl } from '../utils/getGithubUrl';
import { ReactComponent as GoogleLogo } from '../assets/google.svg';
import { ReactComponent as GitHubLogo } from '../assets/github.svg';
import useGdpr from "../hooks/useGdpr";

const LoadingButton = styled(_LoadingButton)`
  padding: 0.6rem 0;
  font-weight: 500;

  &:hover {
    transform: translateY(-2px);
  }
`;

const LinkItem = styled(Link)`
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const loginSchema = object({
    email: string()
        .trim()
        .min(1, msg`Email address is required`)
        .email(msg`Email Address is invalid`),
    password: string()
        .trim()
        .min(1, msg`Password is required`)
        .min(8, msg`Password must be more than 8 characters`)
        .max(32, msg`Password must be less than 32 characters`),
});

export type LoginInput = TypeOf<typeof loginSchema>;

const LoginPage = () => {
    
    useTitle('Login');

    const {accepted} = useGdpr()
    
    const methods = useForm<LoginInput>({
        resolver: zodResolver(loginSchema),
    });

    const { isAuthenticated, handleLogin, loginLoading } = useAuth();
    const navigate = useNavigate();

    const location = useLocation();

    const from = ((location.state as any)?.from?.pathname as string) ?? '/dashboard';
    console.log('LoginPage from', from)

    useEffect(() => {
        if (isAuthenticated && from) {
            navigate(from)
        }
    }, [isAuthenticated, from]);

    const {
        reset,
        handleSubmit,
        formState: { isSubmitSuccessful },
    } = methods;

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitSuccessful]);

    const onSubmitHandler: SubmitHandler<LoginInput> = (values) => {
        // 👇 Executing the loginUser Mutation
        handleLogin(values, from);
    };

    return (
      <Paper sx={ { padding: "2em" } }>
        
        <Container
            maxWidth={false}
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
        
                <Typography
                 
                  component='h2'
                  variant='h2' sx={{ textAlign: "center" }}>
                    <Trans>Welcome Back!</Trans>
                </Typography>
                <Typography
                    variant='body1'
                    component='h2'
                >
                    <Trans>Login to have access!</Trans>
                </Typography>

                <FormProvider {...methods}>
                    <Box
                        component='form'
                        onSubmit={handleSubmit(onSubmitHandler)}
                        noValidate
                        autoComplete='off'
                        maxWidth='27rem'
                        width='100%'
                        sx={ {
                            p: { xs: '1rem', sm: '2rem' },
                            borderRadius: 2,
                        } }
                    >
                        <LoginFormInput name='email' label={t`Email Address`} type='email' />
                        <LoginFormInput name='password' label={t`Password`} type='password' />

                        <Typography
                            sx={ { fontSize: '0.9rem', mb: '1rem', textAlign: 'right' } }
                        >
                            <LinkItem component={RouterLink} to='/forgot-password'>
                                <Trans>Forgot Password?</Trans>
                            </LinkItem>
                        </Typography>

                        <LoadingButton
                            variant='contained'
                            sx={ { mt: 1 } }
                            fullWidth
                            disableElevation
                            type='submit'
                            loading={loginLoading}
                            disabled={ !accepted }
                        >
                            Login
                        </LoadingButton>
                        
                    </Box>
                </FormProvider>
                
                <Typography
                    variant='h6'
                    component='p'
                    sx={{
                        my: '1.5rem',
                        textAlign: 'center',
                    }}
                >
                    <Trans>Log in with another provider</Trans>:
                </Typography>
                <Box
                    maxWidth='27rem'
                    width='100%'
                    sx={ {
                        p: { xs: '1rem', sm: '2rem' },
                        borderRadius: 2,
                    } }
                >
                    {/*<MuiLink*/}
                    {/*    href={getGoogleUrl(from)}*/}
                    {/*    sx={{*/}
                    {/*        borderRadius: 1,*/}
                    {/*        py: '0.6rem',*/}
                    {/*        columnGap: '1rem',*/}
                    {/*        textDecoration: 'none',*/}
                    {/*        cursor: 'pointer',*/}
                    {/*        fontWeight: 500,*/}
                    {/*        '&:hover': {*/}
                    {/*            boxShadow: '0 1px 13px 0 rgb(0 0 0 / 15%)',*/}
                    {/*        },*/}
                    {/*    }}*/}
                    {/*    display='flex'*/}
                    {/*    justifyContent='center'*/}
                    {/*    alignItems='center'*/}
                    {/*>*/}
                    {/*    /!*<GoogleLogo style={{ height: '2rem' }} />*!/*/}
                    {/*    Google*/}
                    {/*</MuiLink>*/}
                    {/*<MuiLink*/}
                    {/*    href={getGitHubUrl(from)}*/}
                    {/*    sx={{*/}
                    {/*        borderRadius: 1,*/}
                    {/*        py: '0.6rem',*/}
                    {/*        mt: '1.5rem',*/}
                    {/*        columnGap: '1rem',*/}
                    {/*        textDecoration: 'none',*/}
                    {/*        cursor: 'pointer',*/}
                    {/*        fontWeight: 500,*/}
                    {/*        '&:hover': {*/}
                    {/*            boxShadow: '0 1px 13px 0 rgb(0 0 0 / 15%)',*/}
                    {/*        },*/}
                    {/*    }}*/}
                    {/*    display='flex'*/}
                    {/*    justifyContent='center'*/}
                    {/*    alignItems='center'*/}
                    {/*>*/}
                    {/*    /!*<GitHubLogo style={{ height: '2rem' }} />*!/*/}
                    {/*    GitHub*/}
                    {/*</MuiLink>*/}
                </Box>
                <Typography variant='body2' sx={{ mt: 2, color: 'text.secondary' }}>
                    <Trans>By logging in you agree to our</Trans> <LinkItem component={RouterLink} to='/terms'><Trans>Terms</Trans></LinkItem> <Trans>and</Trans> <LinkItem component={RouterLink} to='/privacy'><Trans>Privacy Policy</Trans></LinkItem>.
                </Typography>
            </Box>
        </Container>
        
      </Paper>
    );
};

export default LoginPage;
